import * as React from "react";
import { useDebounce } from "use-debounce";

import { SHOW_FOCUS_NOT_WORK_HELP_CLICK } from "../../metricsAction";
import { HelpTooltip } from "../../Helpers/HelpTooltip/HelpTooltip";
import { renderChildrenWithIndent } from "../../MultiControls/TableCell/TableUtils";
import {
    FocusServerErrorCode,
    GetByInnResponseInfo,
} from "../../../../../Common/CandyApiClient/Types/Focus/FocusTypes";
import { useMetrics } from "../../Internals/Metrics/MetricsProviderContext";
import { useAsyncEffect } from "../../../../../Common/UseAsyncEffect";
import { nullElement } from "../../TypingUtils";

import {
    ChildrenContainer,
    CompanyName,
    DontWork,
    LoadingIcon,
    NotFoundContainer,
    Root,
    SearchContainer,
} from "./INN.styles";

export interface INNProps {
    autoFocus?: boolean;
    children: JSX.Element;
    isHideOrgName?: boolean;
    value: string;
    onGetOrganizationNameByInn: (inn: string) => Promise<GetByInnResponseInfo>;
}

enum FocusRequestState {
    NotFound,
    NotWork,
    Search,
}

export function INN(props: INNProps): JSX.Element {
    const track = useMetrics();

    const [name, setName] = React.useState<undefined | string>(undefined);
    const [requestState, setRequestState] = React.useState(
        isValidInnLength(props.value) ? FocusRequestState.Search : undefined
    );
    const [showSearchLoader] = useDebounce(requestState === FocusRequestState.Search, 1000);

    useAsyncEffect(
        async (signal: AbortSignal) => {
            await findCompanyName(props.value, signal);
        },
        [props.value]
    );

    const findCompanyName = async (inn: string, signal: AbortSignal): Promise<void> => {
        if (!isValidInnLength(inn)) {
            setName(undefined);
            setRequestState(undefined);
            return;
        }
        setRequestState(FocusRequestState.Search);
        const answer: GetByInnResponseInfo = await props.onGetOrganizationNameByInn(inn);
        if (signal.aborted) {
            return;
        }
        if (answer.value) {
            setName(answer.value);
            setRequestState(undefined);
        } else if (answer.errorCode === FocusServerErrorCode.NoInnError) {
            setName(undefined);
            setRequestState(FocusRequestState.NotFound);
        } else {
            setName(undefined);
            setRequestState(FocusRequestState.NotWork);
        }
    };

    const handleHelpClick = (): void => {
        track(SHOW_FOCUS_NOT_WORK_HELP_CLICK);
    };

    const renderMessage = () => {
        if (name != undefined) {
            return <CompanyName>{name}</CompanyName>;
        } else if (requestState === FocusRequestState.NotFound) {
            return (
                <NotFoundContainer>
                    {props.value.length === 12 ? "ИП не найден в ЕГРИП" : "Организация не найдена в ЕГРЮЛ"}
                </NotFoundContainer>
            );
        } else if (requestState === FocusRequestState.Search && showSearchLoader) {
            return (
                <SearchContainer>
                    <LoadingIcon />
                    Ищем организацию
                </SearchContainer>
            );
        } else if (requestState === FocusRequestState.NotWork) {
            return (
                <DontWork>
                    Не удалось получить название организации
                    <span onClick={handleHelpClick}>
                        <HelpTooltip marginLeft={4} trigger="click" width={280}>
                            {
                                "Название организации берется из\u00A0сервиса Контур.Фокус, который не\u00A0отвечает. Это\u00A0не\u00A0влияет на\u00A0отчет. Отправляйте, если уверены во\u00A0введенных данных."
                            }
                        </HelpTooltip>
                    </span>
                </DontWork>
            );
        }
        return nullElement;
    };

    const children = renderChildrenWithIndent(props.children).map((propsChild: React.ReactNode, index: number) => {
        if (!React.isValidElement(propsChild)) {
            return propsChild;
        }
        return props.autoFocus && index === 0
            ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              React.cloneElement(propsChild, { ...propsChild.props, autoFocus: true, key: index.toString() })
            : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              React.cloneElement(propsChild, { ...propsChild.props, key: index.toString() });
    });

    return (
        <Root>
            <ChildrenContainer>{children}</ChildrenContainer>
            {!props.isHideOrgName && renderMessage()}
        </Root>
    );
}

function isValidInnLength(inn: string): boolean {
    return inn.length === 10 || inn.length === 12;
}
